"use client";

import { useState } from "react";
import { Mail, CheckCircle2, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { TestSlug } from "@/data/tests/types";
import { trackEvent, trackEmailCaptured } from "@/lib/analytics";
import { getStoredFirstTouchAttribution } from "@/lib/attribution";

export interface CaptureEmailInput {
  email: string;
  score: number;
  category: string;
  consent: boolean;
  attribution?: Record<string, unknown>;
  /** Shared event_id between client pixel + server CAPI for dedup. */
  eventId?: string;
  /** Page URL where the lead converted (for CAPI event_source_url). */
  sourceUrl?: string;
}

export interface CaptureEmailResult {
  ok: boolean;
  error?: string;
}

interface EmailCaptureCardProps {
  score: number;
  category: string;
  testSlug: TestSlug;
  /** Server action that captures the lead (test-specific) */
  captureAction: (input: CaptureEmailInput) => Promise<CaptureEmailResult>;
  /** Optional override for headline + body copy */
  headline?: string;
  body?: string;
  accent?: string;
  /** Submit button label override */
  submitLabel?: string;
  /** Tighter padding / smaller type for secondary placements */
  compact?: boolean;
}

export default function EmailCaptureCard({
  score,
  category,
  testSlug,
  captureAction,
  headline = "Păstrează-ți rezultatul",
  body = "Ți-l trimitem pe email, plus câteva resurse scurte și pași concreți în următoarele 2 săptămâni. Te dezabonezi oricând.",
  accent = "#2C969B",
  submitLabel = "Trimite-mi rezultatul",
  compact = false,
}: EmailCaptureCardProps) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email) {
      setError("Introdu un email valid.");
      return;
    }
    if (!consent) {
      setError("Bifează acordul pentru a continua.");
      return;
    }
    setPending(true);

    const attribution = getStoredFirstTouchAttribution() ?? undefined;
    const eventId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const sourceUrl =
      typeof window !== "undefined" ? window.location.href : undefined;

    const payload: CaptureEmailInput = {
      email,
      score,
      category,
      consent,
      attribution: attribution as Record<string, unknown> | undefined,
      eventId,
      sourceUrl,
    };

    try {
      const res = await captureAction(payload);
      if (!res.ok) {
        setError(res.error ?? "Ceva n-a mers. Reîncearcă.");
        setPending(false);
        return;
      }
      trackEvent(`test_${testSlug}_email_captured`, {
        score,
        category,
      });
      // Shared event id with the server-side CAPI copy. Deliberately no score
      // or result category: a test result is health data and never goes to an
      // ad platform.
      trackEmailCaptured("quiz", { email, leadType: `test_${testSlug}`, eventId });
      setDone(true);
    } catch {
      setError("Eroare neașteptată. Reîncearcă peste câteva minute.");
    }
    setPending(false);
  };

  if (done) {
    return (
      <div
        className="rounded-2xl bg-[#FFF9F5] border p-6 sm:p-8 flex items-start gap-4"
        style={{ borderColor: `${accent}4D` }}
      >
        <CheckCircle2
          className="size-6 shrink-0 mt-0.5"
          style={{ color: accent }}
        />
        <div>
          <h3 className="text-lg font-semibold text-[#264653] mb-2">
            Verifică inbox-ul.
          </h3>
          <p className="text-[#264653]/75 text-sm leading-relaxed">
            Ți-am trimis rezultatul detaliat la <strong>{email}</strong>. Dacă
            nu apare în 2 minute, verifică folderul spam.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("rounded-2xl border", compact ? "p-5" : "p-6 sm:p-7")}
      style={{ background: `${accent}14`, borderColor: `${accent}33` }}
    >
      <div className={cn("flex items-start gap-3", compact ? "mb-4" : "mb-5")}>
        <div
          className="rounded-full p-2 shrink-0"
          style={{ background: `${accent}1A` }}
        >
          <Mail className="size-5" style={{ color: accent }} />
        </div>
        <div>
          <h3
            className={cn(
              "font-semibold text-[#264653] mb-1",
              compact ? "text-base sm:text-lg" : "text-lg sm:text-xl",
            )}
          >
            {headline}
          </h3>
          <p className="text-sm text-[#264653]/70 leading-relaxed">{body}</p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <label className="block">
          <span className="sr-only">Email</span>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            placeholder="email@exemplu.ro"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
            required
            className={cn(
              "w-full h-12 px-4 rounded-xl border text-[15px] text-[#264653] placeholder-[#264653]/40 bg-white",
              "border-[#264653]/15 focus:outline-none focus:ring-2",
              "disabled:opacity-60"
            )}
            style={{
              borderColor: "rgba(38,70,83,0.15)",
            }}
          />
        </label>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            disabled={pending}
            className="mt-1 size-4 rounded border-[#264653]/30"
            style={{ accentColor: accent }}
          />
          <span className="text-xs text-[#264653]/65 leading-relaxed">
            Sunt de acord ca Mindtale să-mi trimită rezultatul și emailuri
            relevante. Vezi{" "}
            <a
              href="/politica-gdpr"
              className="underline"
              target="_blank"
              rel="noopener"
              style={{ color: accent }}
            >
              politica de confidențialitate
            </a>
            .
          </span>
        </label>

        {error && (
          <p
            role="alert"
            className="text-sm text-[#B85037] bg-[#DC7050]/10 px-3 py-2 rounded-lg"
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={pending}
          className={cn(
            "h-12 px-6 rounded-full text-white font-semibold text-[15px]",
            "flex items-center justify-center gap-2 transition-all",
            "hover:brightness-110 hover:gap-3",
            "disabled:opacity-60 disabled:cursor-not-allowed",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          )}
          style={{ background: accent }}
        >
          {pending ? "Se trimite..." : submitLabel}
          {!pending && <ArrowRight className="size-4" />}
        </button>
      </form>
    </div>
  );
}
